import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { 
  Eye, 
  Volume2, 
  Contrast, 
  MousePointer, 
  Keyboard,
  Settings,
  ZoomIn,
  Palette
} from 'lucide-react';

interface AccessibilitySettings {
  highContrast: boolean;
  fontSize: number;
  focusIndicators: boolean;
  reducedMotion: boolean;
  screenReader: boolean;
  keyboardNavigation: boolean;
  audioDescriptions: boolean;
  colorBlindMode: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  zoomLevel: number;
  speechRate: number;
}

export const AccessibilityPanel: React.FC = () => {
  const [settings, setSettings] = useState<AccessibilitySettings>({
    highContrast: false,
    fontSize: 16,
    focusIndicators: true,
    reducedMotion: false,
    screenReader: false,
    keyboardNavigation: true,
    audioDescriptions: false,
    colorBlindMode: 'none',
    zoomLevel: 100,
    speechRate: 1
  });

  const [isOpen, setIsOpen] = useState(false);

  // Apply accessibility settings to the document
  useEffect(() => {
    const root = document.documentElement;
    
    // High contrast mode
    if (settings.highContrast) {
      root.style.setProperty('--background', '0 0% 0%');
      root.style.setProperty('--foreground', '0 0% 100%');
      root.style.setProperty('--primary', '210 100% 50%');
    } else {
      root.style.removeProperty('--background');
      root.style.removeProperty('--foreground');
      root.style.removeProperty('--primary');
    }

    // Font size
    root.style.fontSize = `${settings.fontSize}px`;

    // Zoom level
    root.style.zoom = `${settings.zoomLevel}%`;

    // Reduced motion
    if (settings.reducedMotion) {
      root.style.setProperty('--animation-duration', '0s');
      root.style.setProperty('--transition-duration', '0s');
    } else {
      root.style.removeProperty('--animation-duration');
      root.style.removeProperty('--transition-duration');
    }

    // Color blind mode filters
    const body = document.body;
    body.className = body.className.replace(/colorblind-\w+/g, '');
    if (settings.colorBlindMode !== 'none') {
      body.classList.add(`colorblind-${settings.colorBlindMode}`);
    }

  }, [settings]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings({
      highContrast: false,
      fontSize: 16,
      focusIndicators: true,
      reducedMotion: false,
      screenReader: false,
      keyboardNavigation: true,
      audioDescriptions: false,
      colorBlindMode: 'none',
      zoomLevel: 100,
      speechRate: 1
    });
  };

  const colorBlindOptions = [
    { value: 'none', label: 'None' },
    { value: 'protanopia', label: 'Protanopia (Red-blind)' },
    { value: 'deuteranopia', label: 'Deuteranopia (Green-blind)' },
    { value: 'tritanopia', label: 'Tritanopia (Blue-blind)' }
  ];

  return (
    <>
      {/* Floating accessibility button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-50 rounded-full w-12 h-12 p-0"
        aria-label="Open accessibility panel"
      >
        <Settings className="h-6 w-6" />
      </Button>

      {/* Accessibility Panel */}
      {isOpen && (
        <Card className="fixed bottom-20 right-4 z-50 w-80 max-h-96 overflow-y-auto">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Accessibility
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                aria-label="Close accessibility panel"
              >
                ×
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Visual Settings */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Contrast className="h-4 w-4" />
                Visual
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">High Contrast</label>
                  <Switch
                    checked={settings.highContrast}
                    onCheckedChange={(checked) => updateSetting('highContrast', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Font Size: {settings.fontSize}px
                  </label>
                  <Slider
                    value={[settings.fontSize]}
                    onValueChange={([value]) => updateSetting('fontSize', value)}
                    min={12}
                    max={24}
                    step={1}
                    className="w-full"
                    aria-label="Font size"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Zoom: {settings.zoomLevel}%
                  </label>
                  <Slider
                    value={[settings.zoomLevel]}
                    onValueChange={([value]) => updateSetting('zoomLevel', value)}
                    min={75}
                    max={150}
                    step={25}
                    className="w-full"
                    aria-label="Zoom level"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Color Blind Support</label>
                  <select
                    value={settings.colorBlindMode}
                    onChange={(e) => updateSetting('colorBlindMode', e.target.value as any)}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    {colorBlindOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Motion Settings */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <MousePointer className="h-4 w-4" />
                Motion & Interaction
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Reduced Motion</label>
                  <Switch
                    checked={settings.reducedMotion}
                    onCheckedChange={(checked) => updateSetting('reducedMotion', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Focus Indicators</label>
                  <Switch
                    checked={settings.focusIndicators}
                    onCheckedChange={(checked) => updateSetting('focusIndicators', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Keyboard Navigation</label>
                  <Switch
                    checked={settings.keyboardNavigation}
                    onCheckedChange={(checked) => updateSetting('keyboardNavigation', checked)}
                  />
                </div>
              </div>
            </div>

            {/* Audio Settings */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                Audio
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Screen Reader Support</label>
                  <Switch
                    checked={settings.screenReader}
                    onCheckedChange={(checked) => updateSetting('screenReader', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Audio Descriptions</label>
                  <Switch
                    checked={settings.audioDescriptions}
                    onCheckedChange={(checked) => updateSetting('audioDescriptions', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Speech Rate: {settings.speechRate}x
                  </label>
                  <Slider
                    value={[settings.speechRate]}
                    onValueChange={([value]) => updateSetting('speechRate', value)}
                    min={0.5}
                    max={2}
                    step={0.1}
                    className="w-full"
                    aria-label="Speech rate"
                  />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
              <h3 className="font-medium">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateSetting('highContrast', !settings.highContrast);
                    updateSetting('fontSize', 18);
                  }}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Vision
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateSetting('screenReader', true);
                    updateSetting('keyboardNavigation', true);
                  }}
                >
                  <Keyboard className="h-4 w-4 mr-1" />
                  Motor
                </Button>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={resetSettings}
                className="w-full"
              >
                Reset All Settings
              </Button>
            </div>

            {/* Keyboard Shortcuts */}
            <div className="space-y-2">
              <h3 className="font-medium text-sm">Keyboard Shortcuts</h3>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Alt + A: Open accessibility panel</div>
                <div>Alt + C: Toggle high contrast</div>
                <div>Alt + F: Increase font size</div>
                <div>Tab: Navigate between elements</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};